<template>
  <div
    v-if="imagesComp.length"
    class="osk-product-gallery"
    :class="{'osk-product-gallery_full': !!currentImage}"
  >
    <UikitSwiper
      class="osk-product-gallery__thumbs-swiper"
      :space-between="16"
      :slides-per-view="3"
      :slides-per-group="1"
      mousewheel
      free-mode
      direction="vertical"
      @swiper="setThumbsSwiper"
    >
      <UikitSwiperSlide
        v-for="(image, index) in imagesComp"
        :key="index"
      >
        <picture>
          <source
            :srcset="getImgPath(image.path)"
            type="image/webp"
          >
          <img
            :alt="image.alt ?? 'Изображение продукта'"
            :src="image.path"
            class="osk-product-gallery__thumbs-swiper-image"
            loading="lazy"
            @error="onImgError($event, image)"
          >
        </picture>

        <UikitLoaderInner
          class="swiper-lazy-preloader"
          small
        />
      </UikitSwiperSlide>
    </UikitSwiper>

    <UikitSwiper
      class="osk-product-gallery__main-swiper"
      pagination
      :thumbs="{
        swiper: thumbsSwiper,
        autoScrollOffset: 0,
      }"
      :space-between="6"
      :slides-per-view="1"
      :slides-per-group="1"
      :long-swipes-ms="100"
      :css-mode="!isDesktop"
      :zoom="zoom && !isDesktop ? {toggle: true} : undefined"
      @swiper="setMainSwiper"
      @slide-change="handleSlideChange"
    >
      <UikitSwiperSlide
        v-for="(image, index) in imagesComp"
        :key="index"
      >
        <div
          v-if="zoom && !isDesktop"
          class="swiper-zoom-container"
        >
          <img
            :src="image.path"
            :class="{
              'osk-product-gallery__main-swiper-image': true,
              'zoomMe': true,
            }"
            :alt="image.alt ?? 'Изображение продукта'"
            loading="lazy"
            @error="onImgError"
          >

          <UikitLoaderInner
            class="swiper-lazy-preloader"
          />
        </div>

        <div
          v-else
          :class="{
            'osk-product-gallery__main-swiper-content': true,
            'osk-product-gallery__main-swiper-content_full': !!currentImage,
          }"
        >
          <picture
            class="osk-product-gallery__main-swiper-picture"
          >
            <source
              :srcset="getImgPath(image.path)"
              type="image/webp"
            >
            <img
              :src="image.path"
              :class="{
                'osk-product-gallery__main-swiper-image': true,
                'zoomMe': true,
              }"
              :alt="image.alt ?? 'Изображение продукта'"
              loading="lazy"
              @click.stop.prevent="emits('openDialog', image)"
              @error="onImgError"
            >

            <div
              v-if="product?.exclusiveSelectionTime && index === 0"
              class="osk-product-gallery__o-community-product-mark"
            >
              <ProductOCommunityProductMark />
            </div>
          </picture>

          <UikitLoaderInner
            class="swiper-lazy-preloader"
          />

          <div
            v-if="image.comment && !removedComments.includes(image.id!)"
            class="osk-product-gallery__main-swiper-comment"
          >
            {{ image.comment }}

            <UikitIcon
              name="CloseCircle"
              size="s"
              color="white"
              class="osk-product-gallery__main-swiper-icon"
              @click="removeComment(image.id)"
            />
          </div>
        </div>
      </UikitSwiperSlide>
    </UikitSwiper>
  </div>
</template>

<script setup lang="ts">
import {
  Swiper as SwiperClass,
} from 'swiper/types';
import { ProductImageDTO, ProductDTORes } from '~/restAPI/data-contracts';

const props = defineProps<{
  product?: ProductDTORes
  currentImage?: ProductImageDTO
  images?: ProductImageDTO[],
  zoom?: boolean
}>();

const emits = defineEmits<{
  (e: 'openDialog', image: ProductImageDTO): void,
}>();

const { onImgError, getImgPath } = useImgCdnPath();

const removedComments = ref<number[]>([]);

const { isDesktop } = useUiBreakpoints();

const imagesComp = computed(() => {
  if (props.images) return props.images;

  if (!props.product) return [];

  return [
    ...(props.product.images ?? []),
    ...(props.product.defectImages?.map((item) => ({
      ...item,
      order: (props.product?.images?.length ?? 0) + item.order!,
    })) ?? []),
  ];
});

const mainSwiper = ref<SwiperClass | null>(null);
const thumbsSwiper = ref<SwiperClass | null>(null);

async function setThumbsSwiper(swiper: SwiperClass) {
  thumbsSwiper.value = swiper;

  await nextTick();
  swiper.update();
}

async function setMainSwiper(swiper: SwiperClass) {
  mainSwiper.value = swiper;
}

function removeComment(id?: number) {
  if (!id) return;

  removedComments.value.push(id);
}

async function handleSlideChange(swiper: any) {
  if (!thumbsSwiper.value) return;

  await nextTick();

  const index = swiper.activeIndex - 1;

  thumbsSwiper.value.slideTo(index, 200, true);
}

onMounted(() => {
  if (props.currentImage && mainSwiper.value && thumbsSwiper.value) {
    mainSwiper.value.slideTo((props.currentImage.order ?? 1) - 1, 0);
    thumbsSwiper.value.slideTo((props.currentImage.order ?? 1) - 1, 0);
  }
});
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

$slider-height: 633px;

.osk-product-gallery {
  display: flex;

  .swiper-lazy-preloader {
    width: auto;
    height: auto;
    border: 0;
    margin: 0;
    animation: none !important;
    background: rgba(255, 255, 255, 1);
    border-radius: 0;
  }

  &_full {
    position: relative;
    height: 100%;

    .osk-product-gallery__main-swiper {
      &-content {
        padding-bottom: 0;

        @include media-query(lg-and-up) {
          padding-bottom: 0;
        }
      }

      &-image {
        height: 100%;
      }

      .swiper-slide {
        margin-bottom: 48px;
        overflow: hidden;

        @include media-query(lg-and-up) {
          margin-bottom: 0;
        }
      }

      .osk-swiper__block {
        height: 100%;
      }

      .swiper-pagination {
        bottom: 24px !important;
      }
    }
  }

  .swiper-zoom-container {
    position: relative;
  }

  &__main-swiper {
    width: 100%;
    height: 100%;

    @include media-query(lg-and-up) {
      box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.06);
      margin-bottom: 12px;
      border-radius: 2px;
      overflow: hidden;
      margin-left: -8px;
    }

    .swiper-pagination {
      bottom: 0 !important;
      right: 16px !important;
      left: auto !important;
      width: auto !important;

      @include media-query(lg-and-up) {
        display: none;
      }
    }

    .swiper-pagination-bullet {
      width: 6px !important;
      height: 6px !important;
      margin: 0 3px !important;
    }
  }

  &__main-swiper-content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 33px;
    position: relative;
    height: 100%;

    @include media-query(lg-and-up) {
      height: $slider-height;
      padding: 0;
    }

    &_full {
      @include media-query(lg-and-up) {
        height: 100%;
      }
    }
  }

  &__o-community-product-mark {
    position: absolute;
    left: 0;
    bottom: 0;
    margin: 0 0 32px;

    @include media-query(md-and-up) {
      margin: 0 24px 24px;
    }
  }

  &__main-swiper-comment {
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    color: $white;
    padding: 8px 12px;
    background: rgba(0,0,0,.5);
    border-radius: 2px;
    position: absolute;
    width: calc(100% - 32px);
    left: 16px;
    bottom: 34px;
    display: flex;
    justify-content: space-between;

    @include media-query(lg-and-up) {
      bottom: 32px;
      left: 32px;
      border-radius: 100px;
      width: auto;
      display: flex;
      align-items: center;
    }
  }

  &__main-swiper-picture {
    width: 100%;
    height: 100%;
  }

  &__main-swiper-image {
    height: 351px;
    width: 100%;
    object-fit: contain;

    @include media-query(lg-and-up) {
      max-height: 100%;
      width: 100%;
      height: 100%;
    }
  }

  &__main-swiper-icon {
    margin-left: 12px;
    cursor: pointer;
  }

  &__thumbs-swiper {
    opacity: 0;
    visibility: hidden;
    height: 0;
    flex-shrink: 0;
    width: 0;

    @include media-query(lg-and-up) {
      opacity: 1;
      visibility: visible;
      display: block;
      margin-right: 12px;
      margin-top: -12px;
      margin-left: -12px;
      width: 173px;

      .swiper-slide-thumb-active {
        opacity: .3;
      }
    }

    .swiper {
      padding: 12px;

      &:not(.swiper-initialized) {
        .swiper-wrapper {
          display: block;
          height: $slider-height;

          .swiper-slide {
            display: block;
            height: 223px !important;
            margin-bottom: 24px;
          }
        }
      }
    }

    .swiper-wrapper {
      height: $slider-height;
    }

    .swiper-slide {
      box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.06);
      transition: all linear .2s;
      border-radius: 2px;
    }

    .swiper-slide-thumb-active {
      opacity: .3;
    }

    .swiper-scrollbar {
      height: calc(100% - 48px) !important;
      top: 24px !important;
      right: 0 !important;
      background-color: $grey-3;
    }

    .swiper-scrollbar-drag {
      background-color: $grey;
    }
  }

  &__thumbs-swiper-image {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
}
</style>
